import { useState } from "react";
import {
  Ship,
  History as HistoryIcon,
  LayoutTemplate,
  MessageSquareWarning,
  Menu,
  X,
  Palette,
  Download,
  LogIn,
  LogOut,
  UserPlus,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/auth";

function ModeTab({ mode, current, onClick, icon: Icon, label, testId }) {
  const active = current === mode;
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={
        "px-3 py-1.5 mono text-[11px] uppercase tracking-widest transition-colors flex items-center gap-1.5 " +
        (active
          ? "bg-[#2563EB] text-white font-semibold"
          : "text-slate-600 hover:text-slate-900 hover:bg-slate-100")
      }
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

// Domestic mode's tab still shows a truck glyph — a mini inline SVG keeps the
// visual language consistent (no third icon library needed).
function TruckGlyph(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M14 4H3v13h11z" />
      <path d="M14 8h4l3 4v5h-7z" />
      <circle cx="7" cy="19" r="2" fill="currentColor" />
      <circle cx="17" cy="19" r="2" fill="currentColor" />
    </svg>
  );
}

// A separate CTA — bright yellow when no logo saved, muted grey once branded.
function BrandCta({ hasBrand, onClick, orientation = "row" }) {
  const base =
    "mono text-xs uppercase tracking-widest transition-colors inline-flex items-center gap-2 border h-10 px-3 " +
    (orientation === "col" ? "w-full justify-start" : "");
  const cls = hasBrand
    ? base + " border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md"
    : base + " border-yellow-400 bg-yellow-400 text-slate-900 font-bold hover:bg-yellow-300 rounded-md shadow-[0_0_0_3px_rgba(250,204,21,0.18)]";
  return (
    <button
      type="button"
      data-testid="header-brand-cta"
      onClick={onClick}
      className={cls}
      title={hasBrand ? "Update your logo / company info" : "Add your logo so exports go out on your letterhead"}
    >
      <Palette className="w-4 h-4" />
      {hasBrand ? "Brand" : "Add Your Logo"} <span aria-hidden>→</span>
    </button>
  );
}

// Compact action list — used on both desktop (inline row) and mobile (dropdown).
function ActionLinks({
  onOpenTemplates,
  onOpenHistory,
  onOpenBrand,
  onReset,
  hasBrand,
  onAny,
  orientation = "row",
}) {
  const wrap = orientation === "col" ? "flex flex-col gap-2" : "flex items-center gap-2";
  const btnCls =
    orientation === "col"
      ? "text-slate-700 bg-white hover:bg-slate-50 hover:text-[#2563EB] rounded-md border border-slate-200 justify-start w-full"
      : "text-slate-700 bg-white hover:bg-slate-50 hover:text-[#2563EB] rounded-md border border-slate-200";
  const linkCls =
    orientation === "col"
      ? "text-slate-700 bg-white hover:bg-slate-50 hover:text-[#2563EB] rounded-md border border-slate-200 h-10 px-4 inline-flex items-center transition-colors mono text-sm w-full"
      : "text-slate-700 bg-white hover:bg-slate-50 hover:text-[#2563EB] rounded-md border border-slate-200 h-10 px-4 inline-flex items-center transition-colors mono text-sm";
  return (
    <div className={wrap}>
      <BrandCta
        hasBrand={hasBrand}
        onClick={() => {
          onOpenBrand?.();
          onAny?.();
        }}
        orientation={orientation}
      />
      <Button
        data-testid="open-templates-btn"
        variant="ghost"
        className={btnCls}
        onClick={() => {
          onOpenTemplates();
          onAny?.();
        }}
      >
        <LayoutTemplate className="w-4 h-4 mr-2" /> Templates
      </Button>
      <Link
        to="/reports"
        data-testid="open-reports-link"
        className={linkCls}
        onClick={() => onAny?.()}
      >
        <MessageSquareWarning className="w-4 h-4 mr-2" /> Report
      </Link>
      <Button
        data-testid="open-history-btn"
        variant="ghost"
        className={btnCls}
        onClick={() => {
          onOpenHistory();
          onAny?.();
        }}
      >
        <HistoryIcon className="w-4 h-4 mr-2" /> History
      </Button>
      <a
        data-testid="download-brochure-link"
        href="/marketing/cargoload-onepager.pdf"
        target="_blank"
        rel="noreferrer"
        className={linkCls}
        onClick={() => onAny?.()}
        title="Download the CargoLoad Pro one-pager (PDF)"
      >
        <Download className="w-4 h-4 mr-2" /> Brochure
      </a>
      <Button
        data-testid="reset-btn"
        variant="ghost"
        className={
          orientation === "col"
            ? "text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md justify-start w-full"
            : "text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md"
        }
        onClick={() => {
          onReset();
          onAny?.();
        }}
      >
        New
      </Button>
    </div>
  );
}

function UserBadge({ user, onLogin, onSignup, onLogout, orientation = "row" }) {
  const wrap = orientation === "col" ? "w-full" : "";
  if (!user) {
    return (
      <div className={"flex items-center gap-2 " + wrap}>
        <button
          type="button"
          data-testid="header-signup-btn"
          onClick={onSignup}
          className="mono text-xs uppercase tracking-widest inline-flex items-center gap-1.5 border border-[#2563EB] bg-[#2563EB] text-white font-semibold h-10 px-3.5 rounded-md hover:bg-[#1D4ED8] transition-colors shadow-sm"
          title="Create a new account so your logo & data are saved forever"
        >
          <UserPlus className="w-4 h-4" /> Sign Up
        </button>
        <button
          type="button"
          data-testid="header-login-btn"
          onClick={onLogin}
          className="mono text-xs uppercase tracking-widest inline-flex items-center gap-1.5 border border-slate-200 bg-white h-10 px-3.5 rounded-md text-slate-700 hover:border-[#2563EB] hover:text-[#2563EB] transition-colors"
          title="Log in with your existing account"
        >
          <LogIn className="w-4 h-4" /> Login
        </button>
      </div>
    );
  }
  const initial = (user.name || user.email || "?").trim().slice(0, 1).toUpperCase();
  return (
    <div className={"flex items-center gap-2 " + wrap}>
      <div
        data-testid="user-avatar"
        className="w-9 h-9 flex items-center justify-center bg-[#2563EB] text-white font-bold mono text-sm rounded-full ring-2 ring-blue-100"
        title={user.email}
      >
        {initial}
      </div>
      <button
        type="button"
        data-testid="logout-btn"
        onClick={onLogout}
        className="mono text-[10px] uppercase tracking-widest text-slate-500 hover:text-[#2563EB] inline-flex items-center gap-1"
        title="Sign out"
      >
        <LogOut className="w-3.5 h-3.5" /> Out
      </button>
    </div>
  );
}

export default function AppHeader({
  mode,
  onModeChange,
  onOpenHistory,
  onOpenTemplates,
  onOpenBrand,
  onOpenSignup,
  onOpenLogin,
  onReset,
  hasBrand,
}) {
  const modeLabel = mode === "export" ? "Export Vanning Plan" : "Transporter Load Planner";
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();

  return (
    <header className="border-b border-slate-200/70 sticky top-0 z-30 bg-white/85 backdrop-blur-xl shadow-[0_1px_0_0_rgba(15,23,42,0.03)]">
      <div className="px-4 md:px-10 py-3 md:py-4 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 md:gap-3 group min-w-0">
          <Logo size={36} className="transition-transform duration-300 group-hover:scale-105 shrink-0" />
          <div className="min-w-0">
            <div className="heading text-base md:text-xl font-bold tracking-tight truncate text-slate-900">
              CARGOLOAD<span className="text-[#2563EB]">.PRO</span>
            </div>
            <div className="overline hidden sm:block">{modeLabel}</div>
          </div>
        </Link>

        {/* Mode switcher — always visible (even on tiny phones) */}
        <div className="flex items-center border border-slate-200 rounded-md overflow-hidden shrink-0 bg-white">
          <ModeTab
            mode="domestic"
            current={mode}
            onClick={() => onModeChange("domestic")}
            icon={TruckGlyph}
            label="Dom"
            testId="mode-tab-domestic"
          />
          <ModeTab
            mode="export"
            current={mode}
            onClick={() => onModeChange("export")}
            icon={Ship}
            label="Exp"
            testId="mode-tab-export"
          />
        </div>

        {/* Desktop action row */}
        <div className="hidden lg:flex items-center gap-2">
          <ActionLinks
            onOpenTemplates={onOpenTemplates}
            onOpenHistory={onOpenHistory}
            onOpenBrand={onOpenBrand}
            onReset={onReset}
            hasBrand={hasBrand}
          />
          <UserBadge
            user={user}
            onLogin={onOpenLogin}
            onSignup={onOpenSignup}
            onLogout={logout}
          />
        </div>

        {/* Mobile hamburger */}
        <button
          data-testid="mobile-menu-toggle"
          type="button"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          className="lg:hidden text-slate-700 hover:text-[#2563EB] border border-slate-200 bg-white rounded-md h-9 w-9 flex items-center justify-center transition-colors"
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </button>
      </div>

      {/* Mobile action drawer (below the top bar) */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-slate-200 px-4 py-3 bg-[#FFFFFF] flex flex-col gap-2">
          <ActionLinks
            onOpenTemplates={onOpenTemplates}
            onOpenHistory={onOpenHistory}
            onOpenBrand={onOpenBrand}
            onReset={onReset}
            hasBrand={hasBrand}
            onAny={() => setMobileOpen(false)}
            orientation="col"
          />
          <div className="pt-2 mt-1 border-t border-slate-200">
            <UserBadge
              user={user}
              onLogin={onOpenLogin}
              onSignup={onOpenSignup}
              onLogout={logout}
              orientation="col"
            />
          </div>
        </div>
      )}
    </header>
  );
}
